/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The v2 calling bar: full width along the bottom of the app frame, under the
 * sidebar and the page. A skin over the one telephony engine
 * (telephony/TelephonyContext) -- no call logic lives here -- which is mounted
 * above the routes, so a call survives navigation.
 *
 * Idle: presence, line, dial field, call, keypad. Ringing: caller, decline,
 * answer. On a call: timer, caller, mute, keypad, call notes, open call, end.
 * Wrap-up: open call to file the outcome, or skip.
 *
 * Not built, because the backend has no source for them yet: queue depth for
 * agents, missed-call count, hold, transfer, live captions and a Seep
 * next-step suggestion. See docs/ui-v2/PLAN.md.
 */

import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  AlertTriangle,
  ChevronDown,
  ExternalLink,
  Delete,
  Grid3x3,
  Mic,
  MicOff,
  NotebookPen,
  Phone,
  PhoneIncoming,
  PhoneOff,
  Settings,
  Sparkles,
  X,
} from "lucide-react";
import { useAuth } from "auth/Auth";
import { useAiAssistant } from "context/AiAssistantContext";
import useUiVersion from "hooks/useUiVersion";
import { useTelephony } from "telephony/TelephonyContext";
import { AGENT_PRESENCE, CALL_STATE, PRESENCE_META, formatDuration } from "telephony/callStates";
import { Dialpad as DialpadKeys } from "telephony/components";
import { isDialable, sanitizeDialInput, withCountryPrefix } from "telephony/dialNumber";
import { v2 as t, v2Tones } from "styles/tokens/v2";
import { softphoneBarState } from "./softphoneBarRules";
import { callerIdLabel } from "telephony/callerIdLabel";

const BAR_BG = "linear-gradient(90deg, #E3EEFD 0%, #F4F7FC 50%, #FFF3D1 100%)";
const GREEN = "#15803D";
const RED = "#B42318";

const chipSx = {
  height: 34,
  px: 1.25,
  gap: 0.75,
  borderRadius: "17px",
  border: `1px solid ${t.border2}`,
  bgcolor: t.surface,
  color: t.text,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: "inherit",
  whiteSpace: "nowrap",
};

const roundSx = (bg, fg = "#FFFFFF") => ({
  width: 36,
  height: 36,
  borderRadius: "50%",
  bgcolor: bg,
  color: fg,
  "&:hover": { bgcolor: bg, filter: "brightness(0.92)" },
  "&.Mui-disabled": { bgcolor: t.border, color: t.text3 },
});

// The classic theme gives every popover a transparent paper; the keypads
// sit over the page, so they need a solid one.
const keypadPaperSx = (width) => ({ p: 2, width, borderRadius: "14px", bgcolor: t.surface, border: `1px solid ${t.border}`, boxShadow: t.shadowPop });

/** Shared by AppFrame (render or not), the header (Ask Seep placement) and the avatar menu. */
export function useSoftphoneBar() {
  const telephony = useTelephony();
  const { seats, uiPrefs, setUiPref } = useUiVersion();
  const state = softphoneBarState({
    seats,
    settingsChecked: telephony.settingsChecked,
    telephonyEnabled: telephony.enabled,
    settings: telephony.settings,
    userHidden: Boolean(uiPrefs?.softphoneHidden),
    inlineHostActive: telephony.inlineHostActive,
    callState: telephony.callState,
  });
  const setHidden = (hidden) => setUiPref("softphoneHidden", hidden ? true : null).catch(() => {});
  return { ...state, setHidden };
}

function PresenceChip() {
  const { presence, changePresence, callState } = useTelephony();
  const [anchor, setAnchor] = useState(null);
  const meta = PRESENCE_META[presence] || PRESENCE_META.offline;
  const engineOwned = callState !== CALL_STATE.IDLE;
  return (
    <>
      <ButtonBase
        sx={chipSx}
        disabled={engineOwned}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={`Status: ${meta.label}`}
        aria-haspopup="menu"
      >
        <Box component="span" sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: meta.color }} />
        {meta.label}
        {!engineOwned && <ChevronDown size={14} />}
      </ButtonBase>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: "top", horizontal: "left" }} transformOrigin={{ vertical: "bottom", horizontal: "left" }}>
        {AGENT_PRESENCE.map((p) => (
          <MenuItem
            key={p.value}
            selected={p.value === presence}
            onClick={() => {
              setAnchor(null);
              changePresence(p.value);
            }}
          >
            <Box component="span" sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: p.color, mr: 1.25 }} />
            {p.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function LineChip() {
  const { callerIds, fromNumber, setFromNumberId } = useTelephony();
  const [anchor, setAnchor] = useState(null);
  const list = Array.isArray(callerIds) ? callerIds : [];
  if (!list.length) return null;
  const lineLabel = (n) => callerIdLabel(n);
  const label = lineLabel(fromNumber) || "Choose a line";
  return (
    <>
      <ButtonBase sx={{ ...chipSx, border: "none", bgcolor: "transparent", fontWeight: 500 }} onClick={(e) => setAnchor(e.currentTarget)} aria-label="Calling from">
        <Phone size={15} /> {label}
        {list.length > 1 && <ChevronDown size={14} />}
      </ButtonBase>
      {list.length > 1 && (
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: "top", horizontal: "left" }} transformOrigin={{ vertical: "bottom", horizontal: "left" }}>
          {list.map((c) => (
            <MenuItem
              key={c.id}
              selected={String(c.id) === String(fromNumber?.id)}
              onClick={() => {
                setAnchor(null);
                setFromNumberId(c.id);
              }}
            >
              {lineLabel(c)}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
}

function IdleControls() {
  const { isLeader, leaderElsewhere, takeOverSoftphone, registered, presence, placeCall } = useTelephony();
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [keypad, setKeypad] = useState(null);

  const blocked = !isLeader
    ? `The softphone is active in another PearlCRM tab or window${leaderElsewhere ? ` (${leaderElsewhere})` : ""}.`
    : !registered
      ? "Your softphone is still connecting."
      : presence !== "available"
        ? `You are ${(PRESENCE_META[presence]?.label || "offline").toLowerCase()}. Go Available to place calls.`
        : "";
  const canDial = !blocked && !busy && isDialable(number);

  const dial = async () => {
    if (!canDial) return;
    setBusy(true);
    setError("");
    try {
      await placeCall({ to: number });
      setNumber("");
      setKeypad(null);
    } catch (err) {
      setError(err?.message || "The call could not be placed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Box sx={{ flex: "1 1 200px", maxWidth: 360, display: "flex", alignItems: "center", height: 36, px: 1.5, borderRadius: "18px", border: `1px solid ${error ? RED : t.border2}`, bgcolor: t.surface }}>
        <InputBase
          fullWidth
          placeholder="Call a number"
          value={number}
          onChange={(e) => {
            setNumber(sanitizeDialInput(e.target.value));
            setError("");
          }}
          onKeyDown={(e) => e.key === "Enter" && dial()}
          inputProps={{ "aria-label": "Number to call", inputMode: "tel" }}
          sx={{ fontSize: 14, fontFamily: "inherit" }}
        />
      </Box>
      <Tooltip title={error || blocked || (number ? "" : "Type a number to call")}>
        <span>
          <IconButton aria-label="Call" onClick={dial} disabled={!canDial} sx={roundSx(GREEN)}>
            <Phone size={17} />
          </IconButton>
        </span>
      </Tooltip>
      {!isLeader && (
        <Tooltip title={blocked}>
          <ButtonBase sx={{ ...chipSx, color: t.primaryDark, borderColor: t.primary }} onClick={takeOverSoftphone}>
            Use this tab
          </ButtonBase>
        </Tooltip>
      )}
      <ButtonBase sx={chipSx} onClick={(e) => setKeypad(e.currentTarget)} aria-label="Open keypad">
        <Grid3x3 size={15} /> Keypad
      </ButtonBase>
      <Popover open={Boolean(keypad)} anchorEl={keypad} onClose={() => setKeypad(null)} anchorOrigin={{ vertical: "top", horizontal: "center" }} transformOrigin={{ vertical: "bottom", horizontal: "center" }} PaperProps={{ sx: keypadPaperSx(260) }}>
        {/* "+" for an international number (country code next), the number, and delete. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
          <Tooltip title="Country code">
            <span>
              <ButtonBase
                onClick={() => setNumber((n) => withCountryPrefix(n))}
                disabled={number.startsWith("+")}
                aria-label="Add + for the country code"
                sx={{ width: 36, height: 36, borderRadius: "50%", border: `1px solid ${t.border2}`, fontFamily: "inherit", fontSize: 20, fontWeight: 600, color: t.text, "&:hover": { bgcolor: t.hover }, "&.Mui-disabled": { color: t.textDisabled, borderColor: t.border } }}
              >
                +
              </ButtonBase>
            </span>
          </Tooltip>
          <Typography sx={{ flex: 1, minWidth: 0, fontSize: 22, textAlign: "center", minHeight: 30, color: number ? t.text : t.text3, fontVariantNumeric: "tabular-nums", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {number || "+61 …"}
          </Typography>
          <IconButton aria-label="Delete last digit" onClick={() => setNumber((n) => n.slice(0, -1))} disabled={!number} sx={{ width: 36, height: 36, color: t.text2 }}>
            <Delete size={18} />
          </IconButton>
        </Box>
        <DialpadKeys onKey={(k) => setNumber((n) => sanitizeDialInput(n + k))} />
        <ButtonBase onClick={dial} disabled={!canDial} sx={{ mt: 1.5, width: "100%", height: 40, borderRadius: "20px", bgcolor: canDial ? GREEN : t.border, color: "#FFFFFF", fontWeight: 600, fontFamily: "inherit", gap: 1 }}>
          <Phone size={16} /> Call
        </ButtonBase>
        {(error || blocked) && <Typography sx={{ fontSize: 12, color: error ? RED : t.text2, mt: 1 }}>{error || blocked}</Typography>}
        {!isLeader && (
          <ButtonBase onClick={takeOverSoftphone} sx={{ ...chipSx, mt: 1, width: "100%", justifyContent: "center", color: t.primaryDark, borderColor: t.primary }}>
            Use this tab
          </ButtonBase>
        )}
      </Popover>
    </>
  );
}

function CallNotes({ conversationId }) {
  const { notesDraft, saveNotesDraft } = useTelephony();
  const [anchor, setAnchor] = useState(null);
  const value = notesDraft?.[conversationId] || "";
  return (
    <>
      <ButtonBase sx={chipSx} onClick={(e) => setAnchor(e.currentTarget)} aria-label="Call notes">
        <NotebookPen size={15} /> Notes{value ? " •" : ""}
      </ButtonBase>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: "top", horizontal: "center" }} transformOrigin={{ vertical: "bottom", horizontal: "center" }} PaperProps={{ sx: { p: 2, width: 340, borderRadius: "12px" } }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: t.text, mb: 1 }}>Call notes</Typography>
        <TextField
          multiline
          minRows={4}
          fullWidth
          autoFocus
          value={value}
          onChange={(e) => saveNotesDraft(conversationId, e.target.value)}
          placeholder="Kept with this call and offered when you file the outcome."
        />
      </Popover>
    </>
  );
}
CallNotes.propTypes = { conversationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired };

function CallControls() {
  const navigate = useNavigate();
  const { callState, activeCall, durationSec, muted, toggleMute, sendDigits, hangUp, acceptCall, rejectCall, skipWrapUp } = useTelephony();
  const [keypad, setKeypad] = useState(null);
  const ringing = callState === CALL_STATE.RINGING;
  const live = callState === CALL_STATE.CONNECTED || callState === CALL_STATE.ON_HOLD;
  const wrapUp = callState === CALL_STATE.WRAP_UP;
  const counterparty = activeCall?.direction === "outbound" ? activeCall?.to_address : activeCall?.from_address;
  const who = activeCall?.caller_name || counterparty || "Unknown caller";
  const callId = activeCall?.id;

  const pill = ringing
    ? { label: "Incoming call", bg: v2Tones.info.bg, fg: v2Tones.info.fg, Icon: PhoneIncoming }
    : wrapUp
      ? { label: "Wrap-up", bg: v2Tones.purple.bg, fg: v2Tones.purple.fg, Icon: NotebookPen }
      : live
        ? { label: `On call · ${formatDuration(durationSec)}`, bg: RED, fg: "#FFFFFF", Icon: Phone }
        : { label: "Connecting…", bg: v2Tones.neutral.bg, fg: v2Tones.neutral.fg, Icon: Phone };

  return (
    <>
      <Box sx={{ ...chipSx, display: "inline-flex", alignItems: "center", border: "none", bgcolor: pill.bg, color: pill.fg }} role="status" aria-live="polite">
        <pill.Icon size={15} /> {pill.label}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: t.text }} noWrap>{who}</Typography>
        {activeCall?.caller_name && counterparty && <Typography sx={{ fontSize: 12, color: t.text2 }} noWrap>{counterparty}</Typography>}
      </Box>
      <Box sx={{ flex: 1 }} />

      {ringing && (
        <>
          <ButtonBase onClick={rejectCall} sx={{ ...chipSx, borderColor: RED, color: RED }}>
            <PhoneOff size={15} /> Decline
          </ButtonBase>
          <ButtonBase onClick={acceptCall} sx={{ ...chipSx, border: "none", bgcolor: GREEN, color: "#FFFFFF" }}>
            <Phone size={15} /> Answer
          </ButtonBase>
        </>
      )}

      {live && (
        <>
          <Tooltip title={muted ? "Unmute" : "Mute"}>
            <IconButton aria-label={muted ? "Unmute" : "Mute"} aria-pressed={muted} onClick={toggleMute} sx={roundSx(muted ? t.header : t.surface, muted ? "#FFFFFF" : t.text)}>
              {muted ? <MicOff size={17} /> : <Mic size={17} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Keypad">
            <IconButton aria-label="Keypad" onClick={(e) => setKeypad(e.currentTarget)} sx={roundSx(t.surface, t.text)}>
              <Grid3x3 size={17} />
            </IconButton>
          </Tooltip>
          <Popover open={Boolean(keypad)} anchorEl={keypad} onClose={() => setKeypad(null)} anchorOrigin={{ vertical: "top", horizontal: "center" }} transformOrigin={{ vertical: "bottom", horizontal: "center" }} PaperProps={{ sx: keypadPaperSx(240) }}>
            <DialpadKeys onKey={(k) => sendDigits(k)} />
          </Popover>
          {callId && <CallNotes conversationId={callId} />}
        </>
      )}

      {(live || wrapUp) && callId && (
        <ButtonBase sx={chipSx} onClick={() => navigate(`/call-console?call=${callId}`)}>
          <ExternalLink size={15} /> {wrapUp ? "File outcome" : "Open call"}
        </ButtonBase>
      )}
      {live && (
        <ButtonBase onClick={hangUp} sx={{ ...chipSx, border: "none", bgcolor: "#D92D20", color: "#FFFFFF" }} aria-label="End call">
          <PhoneOff size={15} /> End
        </ButtonBase>
      )}
      {wrapUp && (
        <ButtonBase onClick={skipWrapUp} sx={chipSx}>
          Skip wrap-up
        </ButtonBase>
      )}
    </>
  );
}

function BarErrors() {
  const { deviceError, clearError, micError, dismissMicError } = useTelephony();
  const message = micError || deviceError;
  if (!message) return null;
  const text = typeof message === "string" ? message : message?.message || "The softphone hit a problem.";
  return (
    <Box sx={{ ...chipSx, display: "inline-flex", alignItems: "center", border: "none", bgcolor: v2Tones.danger.bg, color: v2Tones.danger.fg, maxWidth: 360 }} role="alert">
      <AlertTriangle size={15} />
      <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{text}</Box>
      <IconButton size="small" aria-label="Dismiss" onClick={micError ? dismissMicError : clearError} sx={{ color: "inherit", p: 0.25 }}>
        <X size={14} />
      </IconButton>
    </Box>
  );
}

function BarMenu({ canHide, onHide }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [anchor, setAnchor] = useState(null);
  const isAdmin = user?.role === "system_admin";
  if (!canHide && !isAdmin) return null;
  return (
    <>
      <Tooltip title="Calling bar settings">
        <IconButton aria-label="Calling bar settings" onClick={(e) => setAnchor(e.currentTarget)} sx={{ width: 34, height: 34, color: t.text2 }}>
          <Settings size={18} />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: "top", horizontal: "right" }} transformOrigin={{ vertical: "bottom", horizontal: "right" }}>
        {canHide && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              onHide();
            }}
          >
            Hide the calling bar
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              navigate("/settings/telephony/twilio");
            }}
          >
            Phone and calling bar settings
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
BarMenu.propTypes = { canHide: PropTypes.bool.isRequired, onHide: PropTypes.func.isRequired };

export default function SoftphoneBar({ canHide, onHide }) {
  const { callState } = useTelephony();
  const ai = useAiAssistant();
  const idle = callState === CALL_STATE.IDLE;
  const content = useMemo(() => (idle ? "idle" : "call"), [idle]);

  return (
    <Box
      component="section"
      aria-label="Calling bar"
      sx={{
        flexShrink: 0,
        minHeight: 52,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "6px 12px",
        px: "14px",
        py: "7px",
        background: BAR_BG,
        borderTop: `1px solid ${t.border}`,
        boxShadow: "0 -2px 8px rgba(14, 35, 72, 0.06)",
        zIndex: 35,
      }}
    >
      <PresenceChip />
      {content === "idle" ? (
        <>
          <LineChip />
          <IdleControls />
          <Box sx={{ flex: 1 }} />
        </>
      ) : (
        <CallControls />
      )}
      <BarErrors />
      {ai.enabled && (
        <ButtonBase
          onClick={ai.toggle}
          aria-pressed={ai.open}
          sx={{ ...chipSx, bgcolor: t.header, color: "#FFFFFF", border: "1px solid rgba(255,212,92,0.75)" }}
        >
          <Sparkles size={15} color={t.gold} /> Ask Seep
        </ButtonBase>
      )}
      <BarMenu canHide={canHide} onHide={onHide} />
    </Box>
  );
}

SoftphoneBar.propTypes = { canHide: PropTypes.bool.isRequired, onHide: PropTypes.func.isRequired };
